import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";

export default function SouthIndian7() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "7");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="1VRWltmPp-A"
        title="Chamber Welcomes Thiyagaraja (Kamavardhini) — Ilaiyaraaja · live, Lydian Nadhaswaram on classical guitar"
        caption="Part 2 carries on transcribing this performance. Listen for the bass melody that answers the guitar, and for how the whole piece stays inside one raga."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/07-how-to-name-it-kamavardhini-pt2-page1.png"
        caption="Jason's chart — how the 72 Melakartas are built: Sa and Pa fixed; three positions each for Ri, Ga, Dha and Ni (with their Western intervals and the enharmonic pairs M2 = dim3, aug2 = m3, M6 = dim7, aug6 = m7); two for Ma; 6 × 2 × 6 = 72. Below: a Janya raga as a subset of its parent, with glides."
      />

      <SISCard label="The big idea" title="Janya ragas — the art of purposeful subtraction">
        <p>
          In Lesson 6 you met the Melakarta system — 72 parent scales, 7 notes each — through Kamavardhini. Now the question is: what happens when a raga uses <strong>only 5 of those 7 notes?</strong> Or only 6? Or uses different notes going <em>up</em> the scale versus going <em>down</em>?
        </p>
        <p>
          The result is a <strong>Janya raga</strong> — literally "derived" or "born from." A Janya draws its notes from a parent Melakarta — sometimes fewer of them, sometimes in a zigzag order, sometimes with different notes going up and coming down. (Kamavardhini itself has only a few minor janya ragas.) Each janya has its own name, its own characteristic phrases, and its own <em>gamakas</em> — the slides and oscillations that ornament its notes.
        </p>
        <p>
          Jason's framework (from Part 1 of this lesson): a Janya raga has directional properties, drops some notes of its parent, and has its own glides. His analogy is tea — <em>"it's like tea prepared from each town of each city of each state"</em>: the same ingredients, prepared slightly differently everywhere, and you come to love one particular version.
        </p>
      </SISCard>

      <SISCard label="Western parallel" title="Modes, pentatonics — and why Janya ragas go further">
        <p>
          You already know pentatonic scales: the minor pentatonic drops the 2nd and ♭6 from natural minor. That's conceptually a Janya — a 5-note subset of a 7-note parent. And you know modes: Dorian, Phrygian, Lydian are each a different starting point within the major scale's 7 notes.
        </p>
        <p>
          Janya ragas take the subset idea further — <strong>and add directionality.</strong> A Janya raga can use <em>different notes ascending versus descending</em>. In Western terms, imagine a scale where you use the raised 7th going up (harmonic minor behaviour) but the natural 7th coming back down. In Carnatic music this is systematic, named, and notated. It's called a <em>vakra</em> (zigzag) raga when the ascent or descent takes a non-linear path through the notes.
        </p>
        <p>
          Compare: the Western melodic minor scale does exactly this — raised 6th and 7th ascending, natural 6th and 7th descending. Carnatic music has many ragas doing variations of this trick.
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="The prominent chords of Kamavardhini — Jason's summary at the end of Part 2 (Sa = E)"
        caption="Every note of these chords comes from E Kamavardhini (E F G♯ A♯ B C D♯): E major; B with a flattened fifth (B D♯ F); C augmented (C E G♯) with B as its major 7th; and G♯ minor, to which Jason suggests adding a 6th. Use them to build your own chord layers under the melody."
        chords={[
          { chord: "E",      rn: "I",        function: "Tonic", pitches: ["E3", "G#3", "B3"] },
          { chord: "B(♭5)",  rn: "V(♭5)",    pitches: ["B3", "D#4", "F4"] },
          { bar: true },
          { chord: "C+maj7", rn: "♭VI+maj7", pitches: ["C4", "E4", "G#4", "B4"] },
          { chord: "G♯m",    rn: "iii",      pitches: ["G#3", "B3", "D#4"] },
        ]}
      />

      <SISCard label="Raga anatomy" title="Five types of Janya — a field guide">
        <p>
          Carnatic theory classifies Janya ragas by their note count and directional behaviour. Knowing these types helps you hear what kind of Janya you're dealing with:
        </p>
        <ul className="mt-2 space-y-2 pl-4 text-[15px]">
          <li><strong>Sampurna (7 notes both ways)</strong> — no notes skipped, but the janya still differs from its parent, for example by taking a zigzag path or by borrowing a note from outside the parent (a <em>bhashanga</em> raga). Carnatic Bhairavi is the classic case: a janya of Natabhairavi that uses Dha₂ going up and Dha₁ coming down.</li>
          <li><strong>Shadava (6 notes)</strong> — one note omitted either ascending or descending. Fewer notes = more focused emotional range.</li>
          <li><strong>Audava (5 notes)</strong> — pentatonic. Often the most "singable" and memorable ragas live here. Hindolam is one (S G₂ M₁ D₁ N₂ — on C: C E♭ F A♭ B♭); Mohanam (S R₂ G₃ P D₂) is the Western major pentatonic.</li>
          <li><strong>Audava-Shadava (5 up, 6 down)</strong> — asymmetric; different character in each direction. (Abheri, a janya of Kharaharapriya, goes further: 5 notes up, all 7 down.)</li>
          <li><strong>Vakra (zigzag)</strong> — notes used out of strict stepwise order, creating characteristic leaps and loops that define the raga's fingerprint.</li>
        </ul>
      </SISCard>

      <SISInfluence
        artists={[
          "Bhairavi (janya of Natabhairavi — Dha₂ up, Dha₁ down)",
          "Abheri (janya of Kharaharapriya — 5 notes up, 7 down)",
          "Hindolam (5-note janya of Natabhairavi — Hindustani Malkauns)",
          "Mohanam (5-note janya of Harikambhoji — the major pentatonic)",
        ]}
      >
        <p>
          Listen to a vocal recording of any of these ragas and notice how the singer ornaments individual notes with <em>gamakas</em> — slides and oscillations that are as much a part of the raga's identity as the scale itself. Then listen back to Lydian Nadhaswaram's guitar: Jason's chart marks the same idea — the glide — as part of what makes a raga a raga.
        </p>
      </SISInfluence>

      <SISCallout label="Practice drill — subset your scale">
        <p>
          Take the Kamavardhini scale from Lesson 6, in the key of the piece: E – F – G♯ – A♯ – B – C – D♯. Now play an ascending line <strong>skipping the F (Ri₁, the ♭2)</strong>: E – G♯ – A♯ – B – C – D♯ – E. Hear how the omission of one note completely changes the mood? That's a 6-note Janya of Kamavardhini. Now skip the G♯ as well — down to 5 notes. Each step removes a colour from the palette. The art is choosing which colour to leave out.
        </p>
      </SISCallout>

      <SISTakeaway>
        <p>
          The Carnatic system of 72 Melakartas + hundreds of Janyas is not a catalogue to memorise — it's a <strong>generative grammar.</strong> Once you understand that a Janya is "a parent scale, with notes left out, reordered or treated differently going up and down," you can work out how any raga relates to its parent. A similar grammar operates in Western music: a pentatonic scale is a subset of a seven-note parent, and every mode is an alternate starting point within its parent. Carnatic theory just names these relationships explicitly and exhaustively.
        </p>
      </SISTakeaway>
    </LessonShell>
  );
}
